/* eslint-disable complexity */
import {
    AuthorTypeOptionsWithRealtor, NewBuilding, RealtyObjectType, Renovation, Rooms, SellType, BuildEpoch
} from '@search/filter-enums/enums';
import { DealType } from '@search/filter-enums/enums/DealType';
import { RoomsOfferedOptions } from '@search/filter-enums/enums/RoomsOfferredOptions';
import { ISearchType } from '@search/offer-search-routes/src';

import { priceByMortgagePayment } from '../../../../view/filters/helpers/priceHelper';
import {
    ProfessionalSearchInput, SortInput
} from './SearchOffersQuery/__generated__/SearchOffersQuery.graphql';

function enumToGqlRequired<Enum extends Object>(all: Enum, input: Enum[keyof Enum]): keyof Enum {
    const keys = Object.keys(all) as unknown as (keyof Enum)[];

    for (const key of keys) {
        if (all[key] === input) return key;
    }

    throw new Error('Unknown enum: ' + input);
}

const sortToGqlMap = {
    price: { direction: 'ASC', field: 'PRICE' },
    price_desc: { direction: 'DESC', field: 'PRICE' },
    priceSqMeter: { direction: 'ASC', field: 'PRICE_SQ_METER' },
    priceSqMeter_desc: { direction: 'DESC', field: 'PRICE_SQ_METER' },
    date: { direction: 'DESC', field: 'CREATE_DATE' },
    date_asc: { direction: 'ASC', field: 'CREATE_DATE' },
    floor: { direction: 'ASC', field: 'FLOOR' },
    floor_desc: { direction: 'DESC', field: 'FLOOR' },
    room: { direction: 'ASC', field: 'ROOMS_COUNT' },
    room_desc: { direction: 'DESC', field: 'ROOMS_COUNT' },
    timeToMetro: { direction: 'ASC', field: 'TIME_TO_METRO' },
    timeToMetro_desc: { direction: 'DESC', field: 'TIME_TO_METRO' },
    totalArea: { direction: 'ASC', field: 'TOTAL_AREA' },
    totalArea_desc: { direction: 'DESC', field: 'TOTAL_AREA' },
    distanceToCityCenter: { direction: 'ASC', field: 'DISTANCE_TO_CITY_CENTER' },
    updateDate_desc: { direction: 'DESC', field: 'UPDATE_DATE' },
    updateDate: { direction: 'DESC', field: 'UPDATE_DATE' },
    unknown: { direction: 'DESC', field: 'RELEVANCE' }
} as const;

export function offerGqlSortFromRoute(sort: ISearchType['sort']): SortInput {
    return sortToGqlMap[sort ?? 'unknown'];
}

// eslint-disable-next-line complexity
export function offerFilterGqlFromRoute(params: ISearchType): ProfessionalSearchInput {
    const bounds = params.mapBounds?.bounds;

    const priceMin = params.mortgagePayment ? undefined : (params.priceMin ?? undefined);
    const priceMax = params.mortgagePayment ? priceByMortgagePayment(params.mortgagePayment) : (params.priceMax ?? undefined);

    return {
        ids: params.selectedOffers,
        regionId: params.region,
        geoSlugs: params.geoSlugs as string[] | undefined,
        addressIds: params.addresses,
        districtIds: params.districtIds,
        dealType: enumToGqlRequired(DealType, params.dealType),
        realtyObject: enumToGqlRequired(RealtyObjectType, params.categoryType),
        author: params.author
            .filter(item => item !== AuthorTypeOptionsWithRealtor.REALTOR)
            .map(item => enumToGqlRequired(AuthorTypeOptionsWithRealtor, item)),
        balcony: unkToUndef(params.balcony) as ProfessionalSearchInput['balcony'],
        bankPledgeOptions: unkToUndef(params.bankPledgeOptions),
        seoMortgageAllowed: params.seoMortgageAllowed,
        bathroom: params.bathroom,
        bathroomLocation: params.bathroomLocation as ProfessionalSearchInput['bathroomLocation'],
        bounds: bounds ? {
            lx: bounds.min.x,
            ly: bounds.min.y,
            rx: bounds.max.x,
            ry: bounds.max.y
        } : undefined,
        builtYearMax: params.builtYearMax ?? undefined,
        builtYearMin: params.builtYearMin ?? undefined,
        ceilingHeight: params.cellHeight ?
            [ params.cellHeight ] as ProfessionalSearchInput['ceilingHeight'] :
            undefined,
        commisionType: unkToUndef(params.commisionType) as ProfessionalSearchInput['commisionType'],
        depositType: unkToUndef(params.depositType) as ProfessionalSearchInput['depositType'],
        electricity: params.electricity as ProfessionalSearchInput['electricity'],

        floorMax: params.floorMax ?? undefined,
        floorMin: params.floorMin ?? undefined,
        buildingFloorMax: params.floorMax ?? undefined,
        buildingFloorMin: params.floorMin ?? undefined,
        floorFirst: unkToUndef(params.first) as ProfessionalSearchInput['floorFirst'],
        floorLast: unkToUndef(params.last) as ProfessionalSearchInput['floorLast'],
        floorsTotalMax: params.floorsTotalMax ?? undefined,
        floorsTotalMin: params.floorsTotalMin ?? undefined,

        furniture: params.furniture,
        gas: params.gas as ProfessionalSearchInput['gas'],
        geoHash: params.mapBounds?.geohash ?? undefined,
        heating: params.heating as ProfessionalSearchInput['heating'],
        houseAreaMax: params.houseAreaMax ?? undefined,
        houseAreaMin: params.houseAreaMin ?? undefined,
        houseCategory: params.houseCategory as ProfessionalSearchInput['houseCategory'],
        infrastructure: params.infrastructure as ProfessionalSearchInput['infrastructure'],
        isApartments: unkToUndef(params.apartments) as ProfessionalSearchInput['isApartments'],
        kitchenAreaMax: params.kitchenAreaMax ?? undefined,
        kitchenAreaMin: params.kitchenAreaMin ?? undefined,
        landAreaMax: params.landAreaMax ?? undefined,
        landAreaMin: params.landAreaMin ?? undefined,
        landStatus: params.landStatus as ProfessionalSearchInput['landStatus'],
        lift: unkToUndef(params.hasLift) as ProfessionalSearchInput['lift'],
        livingAreaMax: params.livingAreaMax ?? undefined,
        livingAreaMin: params.livingAreaMin ?? undefined,
        livingFacilityOptions: params.livingFacility,
        livingTenantOptions: params.livingTenantOptions,
        metroIds: params.metroIds ?? [],
        newBuilding: params.newBuilding?.map(item => enumToGqlRequired(NewBuilding, item)),
        newBuildingIds: params.newBuildingIds ?? [],
        parking: params.parkings ?? [],
        photo: unkToUndef(params.photoOptions) as ProfessionalSearchInput['photo'],
        layoutImageOptions: params.layoutImageOptions ?? [],
        polygon: params.mapBounds?.polygon ?? [],
        priceMax,
        priceMin,
        mortgagePayment: params.mortgagePayment ?? undefined,
        priceUnit: unkToUndef(params.priceUnit === 'MORTGAGE_PAYMENT' ? 'UNKNOWN' : params.priceUnit, 'ALL') as ProfessionalSearchInput['priceUnit'],
        renovation: params.renovation?.map(item => enumToGqlRequired(Renovation, item)),
        rentPrepayment: (params.rentPrepayment ?? undefined) as ProfessionalSearchInput['rentPrepayment'],
        rooms: params.rooms?.map(item => enumToGqlRequired(Rooms, item)),
        roomsInOffer: params.roomsInOffer?.map(item => enumToGqlRequired(RoomsOfferedOptions, item)),
        sellType: params.sellType.map(item => enumToGqlRequired(SellType, item)),
        sewerage: params.sewerage as ProfessionalSearchInput['sewerage'],
        totalAreaMax: params.totalAreaMax ?? undefined,
        totalAreaMin: params.totalAreaMin ?? undefined,
        userId: params.author.includes(AuthorTypeOptionsWithRealtor.REALTOR) ? params.userId : null,
        transport: unkToUndef(params.transportType ?? params.transport) as ProfessionalSearchInput['transport'],
        transportTimeLimit: unkToUndef(
            params.transportTimeLimit ?? params.timeLimit
        ) as ProfessionalSearchInput['transportTimeLimit'],
        utilitiesIncluded: params.utilitiesIncluded ?? undefined,
        walls: params.walls ?? [],
        water: (params.water ?? []) as ProfessionalSearchInput['water'],
        windowsDirection: params.windowsDirection,
        renovationProgram: unkToUndef(params.renovationProgram),
        nearbyOptions: unkToUndef(params.nearbyOptions),
        sellerOrganizationName: unkToUndef(params.sellerOrganizationName),
        buildEpoch: params.buildEpoch ?? [],
        onlineView: unkToUndef(params.onlineView),
        finishingType: params.finishingType ?? [],
        commissioning: params.commissioning ?? undefined
    };
}

function unkToUndef<Some extends string>(s: Some | null | undefined, key = 'UNKNOWN') {
    return s === key ? undefined : s ?? undefined;
}
